/**
 * The attention tray and the notifications panel.
 *
 * These are two different things and the difference matters. A **notification** is addressed to you. An
 * **attention item** is addressed to whoever holds a permission — it is work the business needs done, and
 * it stays open until someone resolves it, whether or not the person who first saw it is still here. The
 * attention engine is what stops a failed sync or an unexplained stock variance from depending on one
 * person happening to notice (BRIEF §37).
 *
 * Both read real rows. Neither ever shows a placeholder count.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Bell, CheckCircle2, CircleAlert, Info, ShieldAlert } from 'lucide-react';
import { supabase, rpc } from '../lib/supabase';
import { useSession } from '../lib/session';
import { Button, EmptyState, LoadingRows, StatusBadge, cx } from '../ui/primitives';
import { Drawer } from '../ui/overlays';
import { ConfirmDialog } from '../ui/overlays';
import { useState } from 'react';
import { date as fmtDate, dateTime as fmtDateTime } from '../ui/format';

export interface AttentionItem {
  id: string;
  rule_code: string | null;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
  title: string;
  detail: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  /** Where the item leads: the register that acts on it (`workflow.attention_path`), never a page that does not exist. */
  link_path?: string | null;
}

const SEVERITY = {
  CRITICAL: { tone: 'refused' as const, icon: ShieldAlert, label: 'Critical' },
  HIGH: { tone: 'refused' as const, icon: AlertTriangle, label: 'High' },
  MEDIUM: { tone: 'queued' as const, icon: CircleAlert, label: 'Medium' },
  LOW: { tone: 'neutral' as const, icon: Info, label: 'Low' },
  INFO: { tone: 'info' as const, icon: Info, label: 'Information' },
};

export function useAttentionItems(limit = 50) {
  const { can } = useSession();
  return useQuery({
    queryKey: ['attention-items', limit],
    enabled: can('attention.view'),
    refetchInterval: 120_000,   // 30 Oct: every open desk asks this all day
    queryFn: async () => {
      const { data, error } = await supabase
        .from('attention_items')
        .select('*')
        .in('status', ['OPEN', 'ACKNOWLEDGED'])
        .order('severity')
        .order('last_seen_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as AttentionItem[];
    },
  });
}

export function AttentionRow({ item, onActed }: { item: AttentionItem; onActed?: () => void }) {
  const [confirming, setConfirming] = useState<'RESOLVE' | 'DISMISS' | null>(null);
  const queryClient = useQueryClient();
  const meta = SEVERITY[item.severity] ?? SEVERITY.INFO;

  const act = async (action: 'ACKNOWLEDGE' | 'RESOLVE' | 'DISMISS', note?: string) => {
    await rpc('update_attention_item', { p_item_id: item.id, p_action: action, p_note: note ?? null });
    await queryClient.invalidateQueries({ queryKey: ['attention-items'] });
    await queryClient.invalidateQueries({ queryKey: ['attention-count'] });
    onActed?.();
  };

  return (
    <li className="border-b border-line px-4 py-2.5 last:border-b-0">
      <div className="flex items-start gap-2">
        <StatusBadge tone={meta.tone} icon={meta.icon}>
          {meta.label}
        </StatusBadge>
        {item.occurrence_count > 1 ? (
          <span className="tabular text-small text-ink-muted" title="How many times this has recurred">
            ×{item.occurrence_count}
          </span>
        ) : null}
        <time className="ml-auto shrink-0 font-mono text-badge text-ink-faint" dateTime={item.last_seen_at}>
          {fmtDateTime(item.last_seen_at)}
        </time>
      </div>

      <p className="mt-1 text-body">{item.title}</p>
      {item.detail ? <p className="mt-0.5 text-small text-ink-muted">{item.detail}</p> : null}

      <div className="mt-1.5 flex items-center gap-2">
        {item.status === 'OPEN' ? (
          <Button size="dense" variant="ghost" onClick={() => void act('ACKNOWLEDGE')}>
            Acknowledge
          </Button>
        ) : (
          <span className="text-small text-ink-muted">Acknowledged</span>
        )}
        <Button size="dense" variant="ghost" onClick={() => setConfirming('RESOLVE')}>
          Resolve
        </Button>
        <Button size="dense" variant="ghost" onClick={() => setConfirming('DISMISS')}>
          Dismiss
        </Button>
        {item.rule_code ? <span className="ml-auto code-text text-ink-faint">{item.rule_code}</span> : null}
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming === 'DISMISS' ? 'Dismiss this attention item' : 'Resolve this attention item'}
        consequence={
          confirming === 'DISMISS'
            ? 'It will be closed without the underlying condition being fixed. If the condition recurs, a new item is raised.'
            : 'It will be closed as handled. The record of it, and your reason, stay in the audit trail.'
        }
        confirmLabel={confirming === 'DISMISS' ? 'Dismiss' : 'Resolve'}
        // Dismissing without fixing anything is exactly the act that needs explaining later.
        requireReason={confirming === 'DISMISS'}
        onConfirm={async (reason) => act(confirming === 'DISMISS' ? 'DISMISS' : 'RESOLVE', reason)}
      />
    </li>
  );
}

export function AttentionTray({ open, onClose }: { open: boolean; onClose: () => void }) {
  const items = useAttentionItems(25);

  return (
    <Drawer open={open} onClose={onClose} title="Attention" width="420px">
      {items.isLoading ? (
        <LoadingRows rows={5} columns={2} />
      ) : items.isError ? (
        <EmptyState title="Could not load attention items" detail={(items.error as Error).message} />
      ) : (items.data ?? []).length === 0 ? (
        <EmptyState
          title="Nothing needs attention"
          detail="Items appear here when the system finds something a person has to decide — a failed sync, a reconciliation mismatch, an unexplained variance."
        />
      ) : (
        <ul>
          {items.data!.map((item) => (
            <AttentionRow key={item.id} item={item} />
          ))}
        </ul>
      )}

      <div className="border-t border-line px-4 py-2">
        <Link to="/attention" onClick={onClose} className="text-small text-brand hover:underline">
          Open the Attention Centre →
        </Link>
      </div>
    </Drawer>
  );
}

export interface NotificationRow {
  id: string;
  title: string;
  body: string | null;
  created_at: string;
  read_at: string | null;
  channel: string;
  /** Appended by migration 47: where it is about, and whether it must be accepted (client answer N-4). */
  location_code?: string | null;
  link_path?: string | null;
  requires_ack?: boolean;
  acknowledged_at?: string | null;
}

export function NotificationsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can, me } = useSession();
  const queryClient = useQueryClient();
  // Reading, and accepting, are facts about ME: a notice to a site is read by many people, each for themselves.
  const mark = async (id: string, acknowledge: boolean) => {
    await rpc('mark_notification', { p_notification_id: id, p_acknowledge: acknowledge });
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    await queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    await queryClient.invalidateQueries({ queryKey: ['control-centre'] });
  };

  const notifications = useQuery({
    queryKey: ['notifications'],
    enabled: open && Boolean(me?.authenticated),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as NotificationRow[];
    },
  });

  return (
    <Drawer open={open} onClose={onClose} title="Notifications" width="420px">
      {!can('attention.view') && !me?.authenticated ? (
        <EmptyState title="Sign in to see your notifications" />
      ) : notifications.isLoading ? (
        <LoadingRows rows={4} columns={2} />
      ) : notifications.isError ? (
        <EmptyState title="Could not load notifications" detail={(notifications.error as Error).message} />
      ) : (notifications.data ?? []).length === 0 ? (
        <EmptyState
          title="No notifications"
          detail="Messages addressed to you appear here. Work the business needs done appears under Attention instead."
        />
      ) : (
        <ul>
          {notifications.data!.map((n) => (
            <li key={n.id} className={cx('border-b border-line px-4 py-2.5 last:border-b-0', !n.read_at && 'bg-brand-soft/40')}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-body">{n.title}</p>
                <time className="shrink-0 font-mono text-badge text-ink-faint" dateTime={n.created_at}>
                  {fmtDate(n.created_at)}
                </time>
              </div>
              {n.body ? <p className="mt-0.5 text-small text-ink-muted">{n.body}</p> : null}
              <div className="mt-1 flex items-center gap-2 text-small">
                {n.location_code ? <span className="code-text text-ink-faint">{n.location_code}</span> : null}
                {n.link_path ? <Link to={n.link_path} onClick={onClose} className="text-brand hover:underline">Open</Link> : null}
                <span className="ml-auto inline-flex gap-1">
                  {n.requires_ack && !n.acknowledged_at ? <Button size="dense" variant="primary" onClick={() => void mark(n.id, true)}>Accept</Button>
                    : n.requires_ack ? <span className="text-accepted">Accepted</span>
                    : !n.read_at ? <Button size="dense" variant="ghost" onClick={() => void mark(n.id, false)}>Mark read</Button> : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

export { Bell, CheckCircle2 };
